// RoughOpeningsDialog.tsx — Up to 4 rough framed openings (rovar)

import DialogBase from './DialogBase';
import { SlotEditor } from './SlotEditor';
import { usePendingSlot } from './usePendingSlot';
import { SizePalette, RO_PRESETS } from '../utils/sizePalette';
import type { SizePreset } from '../utils/sizePalette';
import { useShallowStore } from '../store/useStore';
import type { RoData } from '../types/panelvar';

const FIELDS = [
  { key: 'mark',   label: 'Mark',   type: 'text'   as const },
  { key: 'width',  label: 'Width',  type: 'number' as const, unit: '"', min: 0 },
  { key: 'height', label: 'Height', type: 'number' as const, unit: '"', min: 0 },
  { key: 'x',      label: 'X Ctr',  type: 'number' as const, unit: '"', min: 0 },
  { key: 'y',      label: 'Y Ctr',  type: 'number' as const, unit: '"', min: 0 },
];

export default function RoughOpeningsDialog() {
  const { ui, project, updatePanel } = useShallowStore(s => ({
    ui: s.ui, project: s.project, updatePanel: s.updatePanel,
  }));
  const panel = project?.panels.find(p => p.id === ui.activePanelId);
  const [slots, setSlots] = usePendingSlot<RoData>('rovar', panel?.rovar ?? [],
    (px, py, mark) => ({ mark, x: px, y: py }),
    { mark: '', width: 48, height: 96, x: 0, y: 0 });
  if (!panel) return null;

  const panelW = panel.mpvar.width  || 360;
  const panelH = panel.mpvar.height || 336;
  const ffeY   = -(panel.mpvar.botElev ?? 0) * 12;

  function addFromPalette(p: SizePreset) {
    const data: RoData = {
      mark: '',
      width: p.width, height: p.height,
      x: panelW / 2,
      y: panelH - ffeY - p.height / 2,  // top edge flush with panel top
    };
    setSlots(prev => {
      const idx = prev.findIndex(s => !s.enabled);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { enabled: true, data };
        return next;
      }
      if (prev.length < 4) return [...prev, { enabled: true, data }];
      return prev;
    });
  }

  return (
    <DialogBase title="Rough Openings (4 max)" onOk={() => updatePanel(panel.id, { rovar: slots })}>
      <SizePalette title="Common RO Sizes" presets={RO_PRESETS} onSelect={addFromPalette} />
      <SlotEditor<RoData>
        slots={slots}
        fields={FIELDS}
        maxSlots={4}
        onChange={v => setSlots(v)}
        defaultData={() => ({ mark: '', width: 48, height: 96, x: panelW / 2, y: panelH - ffeY - 48 })}
        label="RO"
        fieldActions={{
          x: { label: 'Ctr', getValue: () => panelW / 2 },
          y: { label: 'Ctr', getValue: () => panelH / 2 - ffeY },
        }}
      />
    </DialogBase>
  );
}
